import { LicenseKey, DomainRegistration, SuperAdminLicense, LicenseStatus, ResellerPlan } from '@/types/licenseKey';

// Empty arrays instead of mock data
let licenseKeys: LicenseKey[] = [];
let domainRegistrations: DomainRegistration[] = [];
let superAdminLicenses: SuperAdminLicense[] = [];

// Generate a random 30-digit alphanumeric license key
export const generateLicenseKey = (): string => {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let key = 'RMBZ-';
  
  // Generate first section (10 chars)
  for (let i = 0; i < 10; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  key += '-';
  
  // Generate second section (10 chars)
  for (let i = 0; i < 10; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  key += '-';
  
  // Generate third section (10 chars)
  for (let i = 0; i < 10; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return key;
};

// Get all license keys
export const getLicenseKeys = (): Promise<LicenseKey[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([...licenseKeys]);
    }, 500);
  });
};

// Get license key by ID
export const getLicenseKeyById = (id: string): Promise<LicenseKey | null> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const key = licenseKeys.find(k => k.id === id) || null;
      resolve(key);
    }, 300);
  });
};

// Create a new license key - now always with 'enterprise' plan
export const createLicenseKey = (data: {
  domain?: string;
  plan: ResellerPlan;
  expiresAt: Date;
  notes?: string;
}): Promise<LicenseKey> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Always use enterprise plan regardless of what was passed
      // BUT - set name to "Reseller Pro" for display purposes
      const plan: ResellerPlan = 'enterprise';
      
      // Set maximum instances to 10 for Reseller Pro plan
      const maxInstances = 10; // Reseller Pro plan default
      
      const newKey: LicenseKey = {
        id: (licenseKeys.length + 1).toString(),
        key: generateLicenseKey(),
        domain: data.domain || '',
        status: 'active',
        plan: plan, // Force enterprise plan
        maxInstances,
        createdAt: new Date(),
        expiresAt: data.expiresAt,
        usedInstances: 0,
        notes: data.notes
      };
      
      licenseKeys.push(newKey);
      resolve(newKey);
    }, 800);
  });
};

// Update a license key
export const updateLicenseKey = (id: string, data: Partial<LicenseKey>): Promise<LicenseKey> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const index = licenseKeys.findIndex(k => k.id === id);
      if (index === -1) {
        reject(new Error('License key not found'));
        return;
      }
      
      // If trying to update plan, ensure it stays as enterprise
      if (data.plan && data.plan !== 'enterprise') {
        data.plan = 'enterprise';
      }
      
      licenseKeys[index] = {
        ...licenseKeys[index],
        ...data
      };
      
      resolve(licenseKeys[index]);
    }, 600);
  });
};

// Get all domain registrations
export const getDomainRegistrations = (): Promise<DomainRegistration[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([...domainRegistrations]);
    }, 500);
  });
};

// Register a new domain
export const registerDomain = (data: {
  domain: string;
  licenseKeyId: string;
}): Promise<DomainRegistration> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // Check if domain is already registered
      if (domainRegistrations.some(d => d.domain === data.domain)) {
        reject(new Error('Domain already registered'));
        return;
      }
      
      // Check if license key exists
      const licenseKey = licenseKeys.find(k => k.id === data.licenseKeyId);
      if (!licenseKey) {
        reject(new Error('License key not found'));
        return;
      }
      
      const newDomain: DomainRegistration = {
        id: (domainRegistrations.length + 1).toString(),
        domain: data.domain,
        status: 'pending',
        licenseKeyId: data.licenseKeyId,
        createdAt: new Date(),
        dnsRecords: [
          { type: 'CNAME', name: data.domain.split('.')[0], value: 'referme.site' },
          { type: 'TXT', name: '_verify', value: `referme-verify=${Math.random().toString(36).substring(2, 10)}` }
        ]
      };
      
      domainRegistrations.push(newDomain);
      
      // Update license key domain
      const keyIndex = licenseKeys.findIndex(k => k.id === data.licenseKeyId);
      licenseKeys[keyIndex] = {
        ...licenseKeys[keyIndex],
        domain: data.domain
      };
      
      resolve(newDomain);
    }, 700);
  });
};

// Verify a domain
export const verifyDomain = (domainId: string): Promise<DomainRegistration> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const index = domainRegistrations.findIndex(d => d.id === domainId);
      if (index === -1) {
        reject(new Error('Domain registration not found'));
        return;
      }
      
      domainRegistrations[index] = {
        ...domainRegistrations[index],
        status: 'active',
        verifiedAt: new Date()
      };
      
      resolve(domainRegistrations[index]);
    }, 1000);
  });
};

// Get all super admin licenses
export const getSuperAdminLicenses = (): Promise<SuperAdminLicense[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([...superAdminLicenses]);
    }, 500);
  });
};

// Assign license to super admin
export const assignLicenseToSuperAdmin = (data: {
  superAdminId: string;
  licenseKeyId: string;
  superAdminName: string;
}): Promise<SuperAdminLicense> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // Check if license key exists
      const licenseKey = licenseKeys.find(k => k.id === data.licenseKeyId);
      if (!licenseKey) {
        reject(new Error('License key not found'));
        return;
      }
      
      // Check if license key is already assigned
      if (licenseKey.superAdminId) {
        reject(new Error('License key already assigned to a Super Admin'));
        return;
      }
      
      // Check if super admin already has a license
      if (superAdminLicenses.some(l => l.superAdminId === data.superAdminId)) {
        reject(new Error('Super Admin already has a license'));
        return;
      }
      
      // Update license key
      const keyIndex = licenseKeys.findIndex(k => k.id === data.licenseKeyId);
      licenseKeys[keyIndex] = {
        ...licenseKeys[keyIndex],
        superAdminId: data.superAdminId,
        superAdminName: data.superAdminName
      };
      
      // Create new super admin license
      const newLicense: SuperAdminLicense = {
        id: (superAdminLicenses.length + 1).toString(),
        superAdminId: data.superAdminId,
        licenseKeyId: data.licenseKeyId,
        domain: licenseKeys[keyIndex].domain,
        activatedAt: new Date(),
        lastVerifiedAt: new Date(),
        isValid: true
      };
      
      superAdminLicenses.push(newLicense);
      
      resolve(newLicense);
    }, 800);
  });
};

// Verify super admin license
export const verifySuperAdminLicense = (superAdminId: string): Promise<{
  isValid: boolean;
  message?: string;
  license?: SuperAdminLicense;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const license = superAdminLicenses.find(l => l.superAdminId === superAdminId);
      if (!license) {
        resolve({
          isValid: false,
          message: 'No license found for this Super Admin'
        });
        return;
      }
      
      const licenseKey = licenseKeys.find(k => k.id === license.licenseKeyId);
      if (!licenseKey) {
        resolve({
          isValid: false,
          message: 'License key not found'
        });
        return;
      }
      
      // Check if license is expired
      if (licenseKey.status !== 'active' || new Date() > licenseKey.expiresAt) {
        // Update license
        const index = superAdminLicenses.findIndex(l => l.id === license.id);
        superAdminLicenses[index] = {
          ...superAdminLicenses[index],
          isValid: false,
          lastVerifiedAt: new Date()
        };
        
        resolve({
          isValid: false,
          message: 'License has expired or is not active',
          license: superAdminLicenses[index]
        });
        return;
      }
      
      // Update verification date
      const index = superAdminLicenses.findIndex(l => l.id === license.id);
      superAdminLicenses[index] = {
        ...superAdminLicenses[index],
        lastVerifiedAt: new Date()
      };
      
      // Update license key last verified date
      const keyIndex = licenseKeys.findIndex(k => k.id === license.licenseKeyId);
      licenseKeys[keyIndex] = {
        ...licenseKeys[keyIndex],
        lastVerified: new Date()
      };
      
      resolve({
        isValid: true,
        license: superAdminLicenses[index]
      });
    }, 600);
  });
};

// Get instance count limits for a super admin
export const getSuperAdminInstanceLimits = (superAdminId: string): Promise<{
  maxInstances: number;
  usedInstances: number;
  remainingInstances: number;
  plan: ResellerPlan;
}> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const license = superAdminLicenses.find(l => l.superAdminId === superAdminId);
      if (!license) {
        reject(new Error('No license found for this Super Admin'));
        return;
      }
      
      const licenseKey = licenseKeys.find(k => k.id === license.licenseKeyId);
      if (!licenseKey) {
        reject(new Error('License key not found'));
        return;
      }
      
      resolve({
        maxInstances: licenseKey.maxInstances,
        usedInstances: licenseKey.usedInstances,
        remainingInstances: licenseKey.maxInstances - licenseKey.usedInstances,
        plan: licenseKey.plan
      });
    }, 400);
  });
};

// Increment used instances for a license key
export const incrementUsedInstances = (licenseKeyId: string): Promise<LicenseKey> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const keyIndex = licenseKeys.findIndex(k => k.id === licenseKeyId);
      if (keyIndex === -1) {
        reject(new Error('License key not found'));
        return;
      }
      
      if (licenseKeys[keyIndex].usedInstances >= licenseKeys[keyIndex].maxInstances) {
        reject(new Error('Maximum instance limit reached for this license'));
        return;
      }
      
      licenseKeys[keyIndex] = {
        ...licenseKeys[keyIndex],
        usedInstances: licenseKeys[keyIndex].usedInstances + 1
      };
      
      resolve(licenseKeys[keyIndex]);
    }, 500);
  });
};

// Decrement used instances for a license key
export const decrementUsedInstances = (licenseKeyId: string): Promise<LicenseKey> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const keyIndex = licenseKeys.findIndex(k => k.id === licenseKeyId);
      if (keyIndex === -1) {
        reject(new Error('License key not found'));
        return;
      }
      
      if (licenseKeys[keyIndex].usedInstances <= 0) {
        reject(new Error('Used instances is already at 0'));
        return;
      }
      
      licenseKeys[keyIndex] = {
        ...licenseKeys[keyIndex],
        usedInstances: licenseKeys[keyIndex].usedInstances - 1
      };
      
      resolve(licenseKeys[keyIndex]);
    }, 500);
  });
};

// Sales report related types and functions
export interface SalesData {
  id: string;
  invoiceId: string;
  requestOrderNumber: string;
  fulfillmentOrderNumber: string;
  superAdminId: string;
  superAdminName: string;
  instanceId?: string;
  instanceName?: string;
  planName: string;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  date: Date;
  status: 'paid' | 'pending' | 'failed';
  isRecurring: boolean;
}

// Mock sales data for demonstration
const mockSalesData: SalesData[] = [
  {
    id: '1',
    invoiceId: 'INV-001',
    requestOrderNumber: 'REQ-001',
    fulfillmentOrderNumber: 'FUL-001',
    superAdminId: 'sa-001',
    superAdminName: 'John Smith',
    instanceId: 'inst-001',
    instanceName: 'Tech Solutions Inc',
    planName: 'Reseller Pro',
    billingCycle: 'yearly',
    amount: 1990,
    date: new Date('2025-04-10'),
    status: 'paid',
    isRecurring: true
  },
  {
    id: '2',
    invoiceId: 'INV-002',
    requestOrderNumber: 'REQ-002',
    fulfillmentOrderNumber: 'FUL-002',
    superAdminId: 'sa-002',
    superAdminName: 'Jane Doe',
    instanceId: 'inst-002',
    instanceName: 'Marketing Experts',
    planName: 'Reseller Pro',
    billingCycle: 'monthly',
    amount: 199,
    date: new Date('2025-04-09'),
    status: 'paid',
    isRecurring: true
  },
  {
    id: '3',
    invoiceId: 'INV-003',
    requestOrderNumber: 'REQ-003',
    fulfillmentOrderNumber: 'FUL-003',
    superAdminId: 'sa-003',
    superAdminName: 'Michael Johnson',
    instanceId: 'inst-003',
    instanceName: 'Healthcare Solutions',
    planName: 'Reseller Pro',
    billingCycle: 'yearly',
    amount: 1990,
    date: new Date('2025-03-15'),
    status: 'paid',
    isRecurring: false
  },
  {
    id: '4',
    invoiceId: 'INV-004',
    requestOrderNumber: 'REQ-004',
    fulfillmentOrderNumber: 'FUL-004',
    superAdminId: 'sa-001',
    superAdminName: 'John Smith',
    instanceId: 'inst-004',
    instanceName: 'Tech Solutions Branch',
    planName: 'Reseller Pro',
    billingCycle: 'monthly',
    amount: 199,
    date: new Date('2025-02-20'),
    status: 'paid',
    isRecurring: true
  },
  {
    id: '5',
    invoiceId: 'INV-005',
    requestOrderNumber: 'REQ-005',
    fulfillmentOrderNumber: 'FUL-005',
    superAdminId: 'sa-004',
    superAdminName: 'Sarah Williams',
    instanceId: 'inst-005',
    instanceName: 'Education Platform',
    planName: 'Reseller Pro',
    billingCycle: 'yearly',
    amount: 1990,
    date: new Date('2024-12-05'),
    status: 'paid',
    isRecurring: false
  }
];

// Get all sales data
export const getSalesData = (): Promise<SalesData[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([...mockSalesData]);
    }, 500);
  });
};

// Get sales data filtered by date range
export const getSalesDataByDateRange = (startDate: Date, endDate: Date): Promise<SalesData[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredData = mockSalesData.filter(sale => {
        return sale.date >= startDate && sale.date <= endDate;
      });
      resolve([...filteredData]);
    }, 500);
  });
};

// Get sales data filtered by super admin
export const getSalesDataBySuperAdmin = (superAdminId: string): Promise<SalesData[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredData = mockSalesData.filter(sale => sale.superAdminId === superAdminId);
      resolve([...filteredData]);
    }, 500);
  });
};

// Get sales data filtered by instance
export const getSalesDataByInstance = (instanceId: string): Promise<SalesData[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredData = mockSalesData.filter(sale => sale.instanceId === instanceId);
      resolve([...filteredData]);
    }, 500);
  });
};

// Get unique super admins from sales data
export const getUniqueSuperAdmins = (): Promise<{ id: string; name: string }[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const uniqueSuperAdmins = Array.from(
        new Map(mockSalesData.map(sale => [sale.superAdminId, { id: sale.superAdminId, name: sale.superAdminName }]))
      ).map(([_, admin]) => admin);
      resolve(uniqueSuperAdmins);
    }, 300);
  });
};

// Get unique instances from sales data
export const getUniqueInstances = (): Promise<{ id: string; name: string }[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const uniqueInstances = Array.from(
        new Map(
          mockSalesData
            .filter(sale => sale.instanceId && sale.instanceName) // Filter out undefined instances
            .map(sale => [sale.instanceId, { id: sale.instanceId!, name: sale.instanceName! }])
        )
      ).map(([_, instance]) => instance);
      resolve(uniqueInstances);
    }, 300);
  });
};
